import { useQuery, useMutation, keepPreviousData } from '@tanstack/react-query';
import type {
  WhoAmIResponse,
  UserLocation,
  PrCodesResponse,
  PrCodeItem,
  PriceBookPrCodesRequest,
  CheckoutDetailRequest,
  CheckoutByShortcodeRequest,
  CheckoutDetailResponse,
  VehicleSearchRequest,
  VehicleSearchResponse,
  VehicleSearchFilters,
  InventoryTableRow,
} from './dto.inventory';
import { transformCheckoutDetail, type TransformedCheckoutDetail } from './transformers.checkout';
import { transformSearchVehicles } from './transformers.search';
import { IMPORTER_API_BASE_PATH, LUPP_QUERY_KEYS } from '../constants.lupp';
import { useStore } from '../../../../store/store';
import { LuppApi } from '../lupp.service';
import { formatLocaleForApi } from '../../../../utils/tools';

/*
|--------------------------------------------------------------------------
| API BASE URLS
|--------------------------------------------------------------------------
*/

const LOCATION_API_URL = import.meta.env.VITE_LOCATION_API_URL;
const CPQ_BASE_URL = import.meta.env.VITE_CPQ_BASE_URL;
const VEHICLE_SEARCH_BASE_URL = import.meta.env.VITE_VEHICLE_SEARCH_BASE_URL;

/*
|--------------------------------------------------------------------------
| GET USER LOCATION
|--------------------------------------------------------------------------
*/

// Default location fallback: 7373 Gateway Blvd, Newark, CA 94560, United States
const DEFAULT_LOCATION: UserLocation = {
  lat: 37.5259,
  long: -122.0232,
  region: 'CA',
  postcode: '94560',
  country: 'US',
};

/**
 * Fetches the user's location based on their IP address
 * Falls back to Newark, CA location if the API fails or returns empty values
 * @param enabled - Whether the query should be enabled
 * @returns React Query result with user location data
 *
 * @example
 * const { data: location } = useGetLocation();
 * // location.lat, location.long, location.region
 */
export const useGetLocation = (enabled = true) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.USER_LOCATION],
    queryFn: async (): Promise<UserLocation> => {
      try {
        const response = await fetch(LOCATION_API_URL, {
          method: 'GET',
          headers: {
            accept: 'application/json',
          },
        });

        if (!response.ok) {
          // Return default location on error
          return DEFAULT_LOCATION;
        }

        const data: WhoAmIResponse = await response.json();

        // Check if the response has valid data - fallback to defaults for empty values
        const lat = data.msg?.Lat ? parseFloat(data.msg.Lat) : DEFAULT_LOCATION.lat;
        const long = data.msg?.Long ? parseFloat(data.msg.Long) : DEFAULT_LOCATION.long;
        const region = data.msg?.Region || DEFAULT_LOCATION.region;
        const postcode = data.msg?.Postcode || DEFAULT_LOCATION.postcode;
        const country = data.msg?.Country || DEFAULT_LOCATION.country;

        // If lat/long are NaN or 0, use defaults
        return {
          lat: isNaN(lat) || lat === 0 ? DEFAULT_LOCATION.lat : lat,
          long: isNaN(long) || long === 0 ? DEFAULT_LOCATION.long : long,
          region,
          postcode,
          country,
        };
      } catch {
        // Return default location on any error (network, parsing, etc.)
        return DEFAULT_LOCATION;
      }
    },
    enabled,
    staleTime: 1000 * 60 * 30, // 30 minutes - location doesn't change often
    gcTime: 1000 * 60 * 60, // 1 hour cache
  });
};

/*
|--------------------------------------------------------------------------
| GET PR CODES (CPQ Configurator)
|--------------------------------------------------------------------------
*/

import { transformPrCodes, type ParsedFilterOptions } from './transformers.inventory';

export type ModelType = 'air' | 'gravity';

/**
 * Fetches prCodes (product codes) for a specific model from the CPQ configurator
 * @param model - The model type ('air' or 'gravity')
 * @param year - The model year (default: '2025' for Air, '2026' for Gravity)
 * @param enabled - Whether the query should be enabled
 * @returns React Query result with parsed filter options
 *
 * @example
 * const { data: prCodes } = useGetPrCodes('air');
 * // prCodes.exteriorColor, prCodes.wheels, etc.
 */
export const useGetPrCodes = (model: ModelType, year?: string, enabled = true) => {
  const locale = useStore((state) => state.locale);

  // prCodes endpoint uses hyphen format (e.g., 'de-de', 'nl-nl')
  // Add '-us' suffix for 2-character locales
  const formattedLocale = locale.length === 2 ? `${locale}-us` : locale;

  // Default year: Air uses 2025, Gravity uses 2026
  const modelYear = year ?? (model === 'air' ? '2025' : '2026');

  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.PR_CODES, model, modelYear, formattedLocale],
    queryFn: async (): Promise<ParsedFilterOptions> => {
      const response = await fetch(
        `${CPQ_BASE_URL}/v1/${formattedLocale}/${model}/${modelYear}/prCodes`,
        {
          method: 'GET',
          headers: {
            accept: '*/*',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch prCodes: ${response.statusText}`);
      }

      const data: PrCodesResponse = await response.json();
      return transformPrCodes(data);
    },
    enabled,
    staleTime: 1000 * 60 * 60, // 1 hour - prCodes don't change often
    gcTime: 1000 * 60 * 60 * 24, // 24 hour cache
  });
};

// Re-export transformers for easy access
export { transformPrCodes, type ParsedFilterOptions } from './transformers.inventory';
export { transformSearchVehicles, transformSearchVehicleToRow } from './transformers.search';

/*
|--------------------------------------------------------------------------
| POST PRICE BOOK PR CODES (Importer)
|--------------------------------------------------------------------------
*/

/**
 * Fetches prCodes from the LUPP price-book endpoint for importer users.
 * All four fields (channel, model, year, locale) are required.
 * @param params - The request parameters
 * @param enabled - Whether the query should be enabled
 * @returns React Query result with parsed filter options
 *
 * @example
 * const { data: prCodes } = useGetPriceBookPrCodes({ channel: 'IMPORTER', model: 'air', year: '2025', locale: 'en-be' });
 */
export const useGetPriceBookPrCodes = (params: PriceBookPrCodesRequest, enabled = true) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.PRICE_BOOK_PR_CODES, params],
    queryFn: async (): Promise<ParsedFilterOptions> => {
      const response = await LuppApi.post<PriceBookPrCodesRequest, Record<string, PrCodeItem>>(
        `/${IMPORTER_API_BASE_PATH}/price-book/prCodes`,
        params
      );
      return transformPrCodes(response);
    },
    enabled,
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });
};

export type { PriceBookPrCodesRequest } from './dto.inventory';

/*
|--------------------------------------------------------------------------
| GET VEHICLE CHECKOUT DETAIL
|--------------------------------------------------------------------------
*/

/**
 * Fetches detailed checkout information for a specific vehicle
 */
export const useGetCheckoutDetail = () => {
  return useMutation({
    mutationFn: async (params: CheckoutDetailRequest): Promise<TransformedCheckoutDetail> => {
      // Get locale at mutation execution time to avoid stale closure
      const locale = useStore.getState().locale;
      const formattedLocale = formatLocaleForApi(locale);

      const axiosResponse = await LuppApi.instance.post<{ data: CheckoutDetailResponse }>(
        `${VEHICLE_SEARCH_BASE_URL}/${formattedLocale}/checkout`,
        params
      );
      return transformCheckoutDetail(axiosResponse.data.data);
    },
  });
};

/**
 * Error codes returned by the checkout detail API
 */
export const CHECKOUT_ERROR_CODES = {
  VEHICLE_IS_NOT_AVAILABLE: 'VEHICLE_IS_NOT_AVAILABLE',
  INVALID_VEHICLE_CONFIGURATION: 'INVALID_VEHICLE_CONFIGURATION',
} as const;

/**
 * Fetches checkout information for a vehicle by shortcode
 */
export const useGetCheckoutByShortcode = () => {
  return useMutation({
    mutationFn: async (params: CheckoutByShortcodeRequest): Promise<TransformedCheckoutDetail> => {
      const locale = useStore.getState().locale;
      const formattedLocale = formatLocaleForApi(locale);

      try {
        const axiosResponse = await LuppApi.instance.post<{ data: CheckoutDetailResponse }>(
          `${VEHICLE_SEARCH_BASE_URL}/${formattedLocale}/checkout`,
          params
        );
        return transformCheckoutDetail(axiosResponse.data.data);
      } catch (err: unknown) {
        const responseData = (err as { response?: { data?: Record<string, unknown> } }).response
          ?.data;
        const errorCode = responseData?.errorCode ?? responseData?.error_code ?? responseData?.code;
        if (errorCode) {
          const error = new Error(String(responseData?.message ?? 'Checkout request failed'));
          (error as Error & { errorCode: string }).errorCode = String(errorCode);
          throw error;
        }
        throw err;
      }
    },
  });
};

/*
|--------------------------------------------------------------------------
| SEARCH VEHICLES (Auctus API)
|--------------------------------------------------------------------------
*/

export type UseSearchVehiclesParams = {
  page?: number;
  size?: number;
  filters?: VehicleSearchFilters;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  enabled?: boolean;
};

export type SearchVehiclesResult = {
  data: InventoryTableRow[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  isFirst: boolean;
  isLast: boolean;
};

/**
 * Search vehicles using the Auctus Vehicle Search API
 * Supports pagination and filtering by vehicle configuration options
 *
 * @example
 * const { data } = useSearchVehicles({
 *   page: 0,
 *   size: 50,
 *   filters: { appearance: 'EXT03G', wheels: 'WH00' }
 * });
 */
export const useSearchVehicles = ({
  page = 0,
  size = 10,
  filters,
  sortBy,
  sortOrder,
  enabled = true,
}: UseSearchVehiclesParams = {}) => {
  const locale = useStore((state) => state.locale);
  const formattedLocale = formatLocaleForApi(locale);
  return useQuery({
    queryKey: [
      LUPP_QUERY_KEYS.VEHICLE_SEARCH,
      formattedLocale,
      page,
      size,
      filters,
      sortBy,
      sortOrder,
    ],
    queryFn: async (): Promise<SearchVehiclesResult> => {
      const requestBody: VehicleSearchRequest = {
        page,
        size,
        ...(filters && { filters }),
        ...(sortBy && { sortBy, sortOrder }),
      };

      const axiosResponse = await LuppApi.instance.post<{ data: VehicleSearchResponse }>(
        `${VEHICLE_SEARCH_BASE_URL}/${formattedLocale}/search`,
        requestBody
      );
      const data = axiosResponse.data.data;

      return {
        data: transformSearchVehicles(data.content),
        totalElements: data.totalElements,
        totalPages: data.totalPages,
        currentPage: data.number,
        pageSize: data.size,
        isFirst: data.first,
        isLast: data.last,
      };
    },
    enabled,
    placeholderData: keepPreviousData,
  });
};
